import React, { useState } from "react";
import {
  Box,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  Button,
  useToast,
  VStack,
  HStack,
  Divider,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Checkbox,
  CheckboxGroup,
  Stack,
  Progress,
  Badge,
  Flex,
  Spacer,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Icon,
} from "@chakra-ui/react";
import { 
  SecondaryButton, 
  PrimaryButton,
  RadioButtonGroup,
  RadioButton,
  CustomTextArea,
  Panel
} from "../../components/design_library";
import { cancelSubscription, CancellationRequestData, saveCancellationFeedback } from "../../api/CancelSubscription";
import { useAtom } from "jotai";
import { AppStateAtom } from "../../store";
import { useUser } from "../../hooks/useUser";
import { useSettingsController } from "./useSettingsController";
import { trackMetaCustomEvent } from "../../utils/metaPixel";
import { generateEventId } from "../../utils/attribution";

interface CancelSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CancelSubscriptionModal: React.FC<CancelSubscriptionModalProps> = ({ isOpen, onClose }) => {
  // Form state
  const [currentStep, setCurrentStep] = useState(1);
  const [reason, setReason] = useState("");
  const [subReason, setSubReason] = useState("");
  const [usageFrequency, setUsageFrequency] = useState("");
  const [satisfactionRatings, setSatisfactionRatings] = useState({
    overall: "",
    features: "",
    pricing: "",
    support: "",
    ui: ""
  });
  const [missingFeatures, setMissingFeatures] = useState<string[]>([]);
  const [alternativeService, setAlternativeService] = useState("");
  const [detailedFeedback, setDetailedFeedback] = useState("");
  const [allowFollowUp, setAllowFollowUp] = useState(false);
  const toast = useToast();

  // State management hooks
  const [appState, setAppState] = useAtom(AppStateAtom);
  const user = useUser();
  const { requestStripePortal } = useSettingsController();

  const totalSteps = 4;
  const progressPercentage = (currentStep / totalSteps) * 100;

  // Sub-reason options based on primary reason
  const getSubReasonOptions = () => {
    switch (reason) {
      case "pricing":
        return [
          "Too expensive for my budget",
          "Not enough value for the price",
          "Found a cheaper alternative",
          "Free tier limitations too restrictive",
          "Pricing structure is confusing"
        ];
      case "features":
        return [
          "Missing key features I need",
          "Features are too basic",
          "Features don't work as expected",
          "Interface is hard to use",
          "Performance issues"
        ];
      case "switching":
        return [
          "Found a better alternative",
          "Employer provides different tool",
          "Consolidating to fewer services",
          "Better integration with other tools"
        ];
      case "technical":
        return [
          "Frequent bugs or crashes",
          "Slow performance",
          "Data sync issues",
          "Mobile app problems",
          "Integration issues"
        ];
      case "usage":
        return [
          "Don't have time to use it",
          "My needs have changed",
          "Too complicated for my needs",
          "Found manual methods work better"
        ];
      default:
        return [];
    }
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const resetForm = () => {
    setCurrentStep(1);
    setReason("");
    setSubReason("");
    setUsageFrequency("");
    setSatisfactionRatings({
      overall: "",
      features: "",
      pricing: "",
      support: "",
      ui: ""
    });
    setMissingFeatures([]);
    setAlternativeService("");
    setDetailedFeedback("");
    setAllowFollowUp(false);
  };

  const validateCurrentStep = () => {
    switch (currentStep) {
      case 1:
        if (!reason) {
          toast({
            title: "Please select a reason",
            description: "This helps us understand how to improve our service.",
            status: "warning",
            duration: 3000,
            isClosable: true,
          });
          return false;
        }
        return true;
      case 2:
        if (reason !== "other" && !subReason) {
          toast({
            title: "Please provide more details",
            description: "This specific information helps us address common issues.",
            status: "warning",
            duration: 3000,
            isClosable: true,
          });
          return false;
        }
        return true;
      case 3:
        const hasRatings = Object.values(satisfactionRatings).some(rating => rating !== "");
        if (!hasRatings) {
          toast({
            title: "Please rate at least one aspect",
            description: "Your ratings help us prioritize improvements.",
            status: "warning",
            duration: 3000,
            isClosable: true,
          });
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    if (!detailedFeedback || detailedFeedback.length < 20) {
      toast({
        title: "Please provide detailed feedback",
        description: "At least 20 characters help us understand your experience better.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Generate event ID for Meta deduplication
    const metaEventId = generateEventId();

    // Track Meta Pixel custom cancellation event
    trackMetaCustomEvent(
      "CancelSubscriptionRequested",
      {
        reason: reason,
        sub_reason: subReason,
        usage_frequency: usageFrequency,
        detailed_feedback_length: detailedFeedback.length,
      },
      metaEventId
    );

    // Build feedback data to persist before redirecting to Stripe
    const cancellationData: CancellationRequestData = {
      primaryReason: reason,
      subReason: subReason || undefined,
      usageFrequency: usageFrequency || undefined,
      satisfactionRatings: Object.keys(satisfactionRatings).some(key => satisfactionRatings[key as keyof typeof satisfactionRatings]) 
        ? satisfactionRatings 
        : undefined,
      missingFeatures: missingFeatures.length > 0 ? missingFeatures : undefined,
      alternativeService: alternativeService || undefined,
      detailedFeedback: detailedFeedback,
      allowFollowUp: allowFollowUp,
      metaEventId: metaEventId,
    };


    try {
      // Save feedback first
      await saveCancellationFeedback(cancellationData);

      // Notify and redirect to Stripe portal
      toast({
        title: "Redirecting to Stripe Portal",
        description: "Thanks for the feedback. You'll now manage your subscription in Stripe.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });

      // Close modal and redirect
      onClose();
      requestStripePortal();
        
    } catch (error) {
      console.error("Error saving feedback or redirecting to Stripe portal:", error);
      toast({
        title: "Could not process cancellation feedback",
        description: (error as any)?.message || "Please try again or contact support.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <VStack spacing={6} align="stretch">
            <Box>
              <Heading size="md" mb={2}>Why are you canceling?</Heading>
              <Text color="gray.600" mb={4}>
                Help us understand your primary reason for leaving. This information helps us improve our service for everyone.
              </Text>
            </Box>

            <FormControl isRequired>
              <FormLabel>Primary Reason</FormLabel>
              <VStack spacing={3} align="stretch">
                <Button
                  variant={reason === "pricing" ? "solid" : "outline"}
                  onClick={() => setReason("pricing")}
                  size="lg"
                  justifyContent="flex-start"
                  leftIcon={<Text>💰</Text>}
                >
                  Pricing concerns
                </Button>
                <Button
                  variant={reason === "features" ? "solid" : "outline"}
                  onClick={() => setReason("features")}
                  size="lg"
                  justifyContent="flex-start"
                  leftIcon={<Text>⚙️</Text>}
                >
                  Feature limitations
                </Button>
                <Button
                  variant={reason === "switching" ? "solid" : "outline"}
                  onClick={() => setReason("switching")}
                  size="lg"
                  justifyContent="flex-start"
                  leftIcon={<Text>🔄</Text>}
                >
                  Switching to another service
                </Button>
                <Button
                  variant={reason === "technical" ? "solid" : "outline"}
                  onClick={() => setReason("technical")}
                  size="lg"
                  justifyContent="flex-start"
                  leftIcon={<Text>🔧</Text>}
                >
                  Technical issues
                </Button>
                <Button
                  variant={reason === "usage" ? "solid" : "outline"}
                  onClick={() => setReason("usage")}
                  size="lg"
                  justifyContent="flex-start"
                  leftIcon={<Text>📊</Text>}
                >
                  Not using the service enough
                </Button>
                <Button
                  variant={reason === "other" ? "solid" : "outline"}
                  onClick={() => setReason("other")}
                  size="lg"
                  justifyContent="flex-start"
                  leftIcon={<Text>❓</Text>}
                >
                  Other reason
                </Button>
              </VStack>
            </FormControl>

            <FormControl>
              <FormLabel>How often did you use WealthBee?</FormLabel>
              <Select
                placeholder="Select frequency"
                value={usageFrequency}
                onChange={(e) => setUsageFrequency(e.target.value)}
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="rarely">Rarely</option>
                <option value="never">Never used it</option>
              </Select>
            </FormControl>
          </VStack>
        );

      case 2:
        return (
          <VStack spacing={6} align="stretch">
            <Box>
              <Heading size="md" mb={2}>Tell us more about your experience</Heading>
              <Text color="gray.600" mb={4}>
                Your specific feedback helps us address the most common issues.
              </Text>
            </Box>

            {reason !== "other" && (
              <FormControl isRequired>
                <FormLabel>Specifically, what was the issue?</FormLabel>
                <VStack spacing={2} align="stretch">
                  {getSubReasonOptions().map((option) => (
                    <Button
                      key={option}
                      variant={subReason === option ? "solid" : "outline"}
                      onClick={() => setSubReason(option)}
                      size="md"
                      justifyContent="flex-start"
                    >
                      {option}
                    </Button>
                  ))}
                </VStack>
              </FormControl>
            )}

            {reason === "features" && (
              <FormControl>
                <FormLabel>What features were you hoping to find?</FormLabel>
                <CheckboxGroup
                  value={missingFeatures}
                  onChange={(values) => setMissingFeatures(values as string[])}
                >
                  <Stack spacing={2}>
                    <Checkbox value="advanced-analytics">Advanced portfolio analytics</Checkbox>
                    <Checkbox value="tax-optimization">Tax optimization tools</Checkbox>
                    <Checkbox value="automated-rebalancing">Automated portfolio rebalancing</Checkbox>
                    <Checkbox value="options-tracking">Options trading tracking</Checkbox>
                    <Checkbox value="crypto-support">Cryptocurrency support</Checkbox>
                    <Checkbox value="advisor-tools">Financial advisor tools</Checkbox>
                    <Checkbox value="mobile-app">Better mobile app</Checkbox>
                    <Checkbox value="api-access">API access</Checkbox>
                  </Stack>
                </CheckboxGroup>
              </FormControl>
            )}

            {reason === "switching" && (
              <FormControl>
                <FormLabel>Which service are you switching to? (Optional)</FormLabel>
                <Select
                  placeholder="Select alternative or type other"
                  value={alternativeService}
                  onChange={(e) => setAlternativeService(e.target.value)}
                >
                  <option value="personal-capital">Personal Capital</option>
                  <option value="mint">Mint</option>
                  <option value="ynab">YNAB</option>
                  <option value="quicken">Quicken</option>
                  <option value="tiller">Tiller</option>
                  <option value="excel-spreadsheet">Excel/Google Sheets</option>
                  <option value="other">Other</option>
                </Select>
              </FormControl>
            )}
          </VStack>
        );

      case 3:
        return (
          <VStack spacing={6} align="stretch">
            <Box>
              <Heading size="md" mb={2}>Rate your experience</Heading>
              <Text color="gray.600" mb={4}>
                Your ratings help us understand what's working and what needs improvement.
              </Text>
            </Box>

            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>Overall satisfaction (1-5 stars)</FormLabel>
                <HStack spacing={2}>
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <Button
                      key={rating}
                      variant={satisfactionRatings.overall === rating.toString() ? "solid" : "outline"}
                      onClick={() => setSatisfactionRatings(prev => ({ ...prev, overall: rating.toString() }))}
                      size="sm"
                    >
                      {"⭐".repeat(rating)}
                    </Button>
                  ))}
                </HStack>
              </FormControl>

              <FormControl>
                <FormLabel>Feature usefulness</FormLabel>
                <HStack spacing={2}>
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <Button
                      key={rating}
                      variant={satisfactionRatings.features === rating.toString() ? "solid" : "outline"}
                      onClick={() => setSatisfactionRatings(prev => ({ ...prev, features: rating.toString() }))}
                      size="sm"
                    >
                      {rating}
                    </Button>
                  ))}
                </HStack>
              </FormControl>

              <FormControl>
                <FormLabel>Value for money</FormLabel>
                <HStack spacing={2}>
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <Button
                      key={rating}
                      variant={satisfactionRatings.pricing === rating.toString() ? "solid" : "outline"}
                      onClick={() => setSatisfactionRatings(prev => ({ ...prev, pricing: rating.toString() }))}
                      size="sm"
                    >
                      {rating}
                    </Button>
                  ))}
                </HStack>
              </FormControl>

              <FormControl>
                <FormLabel>Customer support</FormLabel>
                <HStack spacing={2}>
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <Button
                      key={rating}
                      variant={satisfactionRatings.support === rating.toString() ? "solid" : "outline"}
                      onClick={() => setSatisfactionRatings(prev => ({ ...prev, support: rating.toString() }))}
                      size="sm"
                    >
                      {rating}
                    </Button>
                  ))}
                </HStack>
              </FormControl>

              <FormControl>
                <FormLabel>User interface</FormLabel>
                <HStack spacing={2}>
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <Button
                      key={rating}
                      variant={satisfactionRatings.ui === rating.toString() ? "solid" : "outline"}
                      onClick={() => setSatisfactionRatings(prev => ({ ...prev, ui: rating.toString() }))}
                      size="sm"
                    >
                      {rating}
                    </Button>
                  ))}
                </HStack>
              </FormControl>
            </VStack>
          </VStack>
        );

      case 4:
        return (
          <VStack spacing={6} align="stretch">
            <Box>
              <Heading size="md" mb={2}>Final thoughts</Heading>
              <Text color="gray.600" mb={4}>
                Share any additional feedback to help us improve and serve future customers better.
              </Text>
            </Box>

            <FormControl isRequired>
              <FormLabel>Detailed feedback (minimum 20 characters)</FormLabel>
              <Textarea
                placeholder="Please share any additional thoughts, suggestions, or specific examples that would help us improve..."
                value={detailedFeedback}
                onChange={(e) => setDetailedFeedback(e.target.value)}
                minHeight="120px"
              />
              <Text fontSize="sm" color="gray.500" mt={1}>
                {detailedFeedback.length}/20 characters minimum
              </Text>
            </FormControl>

            <FormControl>
              <Checkbox
                isChecked={allowFollowUp}
                onChange={(e) => setAllowFollowUp(e.target.checked)}
              >
                I'm open to a brief follow-up conversation to help improve WealthBee
              </Checkbox>
              <Text fontSize="sm" color="gray.500" mt={1}>
                We may reach out within 30 days with questions about your feedback
              </Text>
            </FormControl>

            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle>Next steps...</AlertTitle>
                <AlertDescription>
                  We value your feedback and want to understand your experience. After submitting, you'll be redirected to Stripe where you can manage your subscription directly.
                </AlertDescription>
              </Box>
            </Alert>
          </VStack>
        );

      default:
        return null;
    }
  };

  // Handle modal close with form reset
  const handleModalClose = () => {
    onClose();
    resetForm();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleModalClose} size="4xl" isCentered>
      <ModalOverlay bg="rgba(0, 0, 0, 0.6)" />
      <ModalContent bg="var(--dark-gray)" maxH="90vh" overflowY="auto">
        <ModalHeader>
          <Heading size="lg" color="white">
            Manage Subscription
          </Heading>
          <ModalCloseButton color="white" />
        </ModalHeader>
        
        <ModalBody pb={6}>
          <VStack spacing={6} align="stretch">
            {/* Progress indicator */}
            <Box>
              <Flex justify="space-between" align="center" mb={2}>
                <Text fontSize="sm" color="gray.400">
                  Step {currentStep} of {totalSteps}
                </Text>
                <Badge colorScheme="blue" variant="subtle">
                  {Math.round(progressPercentage)}% Complete
                </Badge>
              </Flex>
              <Progress value={progressPercentage} colorScheme="blue" borderRadius="md" />
            </Box>

            {/* Form content */}
            <Panel>{renderStep()}</Panel>

            {/* Navigation buttons */}
            <HStack justify="space-between">
              <Button
                variant="ghost"
                onClick={handleBack}
                isDisabled={currentStep === 1}
                color="gray.400"
              >
                ← Back
              </Button>
              
              <Spacer />
              
              {currentStep < totalSteps ? (
                <PrimaryButton onClick={handleNext}>
                  Next →
                </PrimaryButton>
              ) : (
                <PrimaryButton onClick={handleSubmit} colorScheme="blue">
                  Continue to Stripe Portal
                </PrimaryButton>
              )}
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

// Trigger component for opening the modal
const CancelSubscription = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <>
      <VStack spacing={4} align="flex-start">
        <Text fontSize="lg" fontWeight="semibold" color="blue.400">
          Manage Subscription
        </Text>
        <Text >
          Need to cancel or modify your subscription? We'll collect your feedback and redirect you to Stripe to manage your subscription.
        </Text>
        <Button
          colorScheme="blue"
          variant="outline"
          onClick={onOpen}
          size="sm"
          _hover={{ bg: "blue.50", borderColor: "blue.400" }}
        >
          Manage Subscription
        </Button>
      </VStack>
      
      <CancelSubscriptionModal isOpen={isOpen} onClose={onClose} />
    </>
  );
};

export default CancelSubscription;
